import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '@codes/ui-kit/components/ui/use-toast';

import { useAgent } from '@codes/agents';
import { useChatMessages, useSendMessageV2 } from '@codes/chatbots';
import { useWebCoreStore } from '@codes/web-core';

import { useChatBase } from './useChatBase';

interface UseMyAgentChatProps {
    agentId: string;
    chatId: string;
}

export const useMyAgentChat = ({ agentId, chatId }: UseMyAgentChatProps) => {
    const { t } = useTranslation();
    const { profile } = useWebCoreStore();

    // State management
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    // Data fetching
    const { data: agentData, isLoading: isAgentLoading } = useAgent(agentId, !!agentId);
    const {
        data: messagesData,
        isLoading: isMessagesLoading,
        fetchNextPage,
        hasNextPage,
    } = useChatMessages({
        rootId: chatId,
        limit: 20,
    });

    const sendMessage = useSendMessageV2();

    // Shared chat base logic
    const {
        displayMessages,
        input,
        setInput,
        isWaitingResponse,
        clearPendingMessage,
        createPendingMessage,
        markPendingAsError,
        updateChatMessages,
    } = useChatBase({ chatId, messagesData, limit: 20 });

    const hasMore = hasNextPage ?? false;

    // Load more messages
    const loadMoreMessages = useCallback(async () => {
        if (isLoadingMore || !hasMore) {
            return;
        }

        try {
            setIsLoadingMore(true);
            await fetchNextPage();
        } catch (error) {
            console.error('Failed to load more messages:', error);
            toast({ title: t('chat.load_more_failed') });
        } finally {
            setIsLoadingMore(false);
        }
    }, [fetchNextPage, isLoadingMore, hasMore, t]);

    // Message handling
    const addMessage = useCallback(
        async (messageContent: string) => {
            const pending = createPendingMessage(messageContent);
            if (!pending) {
                return;
            }

            try {
                const res = await sendMessage.mutateAsync({
                    groupId: chatId,
                    params: { sid: profile.sid },
                    body: {
                        ...(profile?.sid && { sid: profile.sid }),
                        content: messageContent.trim(),
                    },
                });

                await updateChatMessages(chatId, res.list || []);
                clearPendingMessage();
            } catch (error) {
                console.error('Failed to send message:', error);
                markPendingAsError();
                toast({ title: t('chat.send_message_failed') });
            }
        },
        [
            chatId,
            sendMessage,
            profile,
            clearPendingMessage,
            createPendingMessage,
            markPendingAsError,
            updateChatMessages,
            t,
        ]
    );

    return {
        agentData,
        displayMessages,
        input,
        setInput,
        addMessage,
        isLoading: isAgentLoading,
        isMessagesLoading,
        isWaitingResponse,
        isLoadingMore,
        hasMore,
        loadMoreMessages,
    };
};
